import {
  MODERN_COLOR_PALETTE,
  normalizeFieldDef,
  resolveFieldValue,
  type AiOutputFormat,
  type AnalysisAxisRule,
  type AnalysisDisplayRules,
  type ColorRule,
  type ColorRuleBucket,
  contrastTextColor,
  type ColorRuleValue,
  type DistrictMapPmtiles,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { GisFeaturePanel, MapLegend, MapView, useDistrictGisLayers } from '../map';
import type { GisFeatureClickInfo } from '../map';
import { type AiCategoryMastersResult, type PostMinimal } from '../posts';
import {
  DISTRICT_RULE_AXIS,
  buildClassificationMajorAxes,
  buildColorAxisOptions,
  buildSizeAxisOptions,
  collectCategoryMajorValues,
  collectEcwValues,
  collectRepresentativeMajorValues,
  collectSelectValues,
  resolveCategoryColor,
  resolveCategoryMajorColor,
  resolveCategoryMajorLabel,
  resolveEmotionColor,
  resolveMasterMajorColor,
  resolveMasterMajorLabel,
  resolveRepresentativeMajor,
  type MapAxisKind,
  type MapAxisOption,
} from './analysisAxes';
// 分析画面と同じ軸エンジンでフィルタ (master / dataSettings / M5 由来の bucket 絞込)。
import {
  buildAiAxisOptionsWithPerspectives,
  buildAxisGroups,
  buildAxisOptions,
  perspectiveAxisCodes,
  collectCustomAxes,
  collectIncludeEmptyKeys,
  deriveAxisOptionsFromDataSettings,
  postCodesForAxis,
  type AxisGroup,
  type PostFieldAxisGroup,
} from './matrixAxisModel';
import {
  PERSPECTIVE_AXIS_PREFIX,
  resolvePerspectiveAxis,
  type AnalysisPerspective,
} from './analysisPerspectives';

/** classification-major 軸選択時に、各 post の代表 majorCode を注入する派生 extraFields key。 */
const CLS_MAJOR_FIELD_KEY = '__clsMajor';
/** docs/29 §8-1: 追加の観点の軸で色分けするとき、地図に描く投稿の写しに載せる代表値の key。 */
const PERSPECTIVE_AXIS_FIELD_KEY = '__perspectiveAxis';
const EMPTY_KEY_SET = new Set<string>();
const EMPTY_GROUP_MAP = new Map();

/**
 * 分析アプリ「地図」タブ (docs/105 §3 「map タブ全欠落」close、旧 `EcwMapView` の
 * colorAxis / sizeAxis 動的切替を v2 minimum で踏襲)。
 *
 * 旧 `old2/.../components/ecw/EcwMapView.tsx` (L170-245 getPostColor / L543-557 size 正規化) を
 * 参照し、以下を v2 に移植:
 *   - **colorAxis 切替**: マーカー色を「色なし (mode 既定) / 感情 / 分類 / rating-scale (extraFields の
 *     数値 field)」で切替。選択軸から `ColorRule` (kind:'value') を組み立て、features/map の
 *     `MapView` (`colorRule` prop) + `MapLegend` (凡例) にそのまま渡して再利用する。
 *   - **sizeAxis 切替**: マーカーサイズの基準軸を「なし (一律) / extraFields の数値 field」で切替。
 *     docs/107 Wave3: 旧 EcwMapView の absScore/maxAbs 正規化 (6〜24px 可変) を移植。MapView に
 *     `radiusById` (post.id→半径 map) prop を追加し、選択軸の |値|/maxAbs を正規化した半径で
 *     circle-radius を data-driven 描画する (sizeAxis 未選択なら全ピン既定 10px)。
 *
 * M5 (`mapRules`) 連動:
 *   - `mapRules.defaultColorAxis` を colorAxis セレクタの初期値に (axis options に無ければ追加)。
 *   - `mapRules.defaultSizeAxis` を sizeAxis セレクタの初期値に (同上)。
 *   - `mapRules.defaultCenter` / `defaultZoom` を MapView の初期表示に。
 *
 * 地図エンジンは features/map の `MapView` を再利用 (新規エンジンは作らない)。
 * 空状態 (投稿なし / 位置情報なし) は他の分析タブと同じ慣習で中央メッセージを表示。
 */

export interface AnalysisMapViewProps {
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
  posts: PostMinimal[];
  onSelectPost: (post: PostMinimal) => void;
  /**
   * old2 整合 (色の一貫性): emotion / category colorAxis のマーカー色を matrix / 一覧 / 詳細と
   * 同じ M2 master colorMap (`resolveChipColor`) で解決するための resolver。
   * old2 では `useEcwLegend` の `axisColorMaps` が地図ピン色と凡例を master colorMap で統一駆動していた。
   * v2 は当初 frequency 順の `MODERN_COLOR_PALETTE` で色付けしており matrix / 一覧 と不一致だったため、
   * resolver を受けて master 由来色に揃える。未指定時は従来の palette fallback (後方互換、test 用)。
   */
  resolver?: AiCategoryMastersResult;
  /** M5 `analysisDisplayConfigs/{id}.displayRules.map` (未指定なら default 動作)。 */
  mapRules?: AnalysisDisplayRules['map'];
  /**
   * 管理画面で地区に設定した色分けルール (`District.mapDisplay.colorRule`)。
   * 指定すると色分け軸に「地区の色分け設定」が現れ、選ぶとピン・凡例・ヒートマップが
   * **投稿アプリの地図と同じ色**になる。1 軸 (value) でも 2 軸 (quadrant) でも扱える。
   * 軸名・条件式・色はすべて管理画面側の設定なので、本 component は中身を解釈しない。
   */
  districtColorRule?: ColorRule | null;
  /**
   * M5 マトリクス表示ルール。分析画面と同じ軸駆動フィルタ (custom 軸 / hiddenAxisKeys /
   * includeEmpty) を地図にも供給するため。未指定なら master + postField 軸のみでフィルタ。
   */
  matrixRules?: AnalysisDisplayRules['matrix'];
  /** 地区 mode (MapView の mode 既定色 / 凡例 fallback 用)。 */
  mode?: DistrictMode | null;
  /** PMTiles cache lookup の key。 */
  districtId?: string;
  /**
   * docs/107 Wave3: PMTiles メタ (オフライン地図)。MapView へ渡すと cache lookup が走り
   * 「🗺 オフライン」ベース + (offline 時) 自動切替が有効になる。未指定だと MapView は
   * OSM raster 固定 (機内モードで地図が白くなる) — old2 は全マップが pmtiles offline 対応だった。
   */
  pmtilesMeta?: DistrictMapPmtiles | null;
  /**
   * Phase 4 (docs/105): 「地図から粗選択 → 分析タブ」 の handoff。指定すると地図上部に
   * 「分析用にピンを選択」トグルが出て、ON 時はピン click で選択をトグル (詳細 modal は開かない)。
   * 「この選択を分析へ」で選択 post id 群を親 (AnalysisPage) に渡す。
   * 注: 現状 MapView に選択ハイライト prop が無いためピンの色は変わらない (件数で feedback)。
   *     ピンのハイライトは MapView 拡張 sprint で対応 (follow-up)。
   */
  onAnalyzeSelection?: (postIds: string[]) => void;
  /**
   * docs/105 地図軸 parity: 地区 `dataSettings.postFields`。matrix の軸候補 (rating-scale /
   * number postField) と同集合を地図の色 / サイズ軸にも供給するため。未指定なら従来動作
   * (ECW 感情 / 分類 + 素の数値 extraFields のみ)。
   */
  postFields?: FieldDef[];
  /**
   * M3 出力フォーマット (matrix と同じ)。classificationAxes を解決して、任意 mode (keikan/custom)
   * の「分類 大分類」色軸 (master colorMap 色) を地図にも供給するため。未指定なら従来の
   * ECW 感情/分類 + postField 軸のみ。
   */
  outputFormat?: AiOutputFormat | null;
  /** docs/22 GIS (要件4): 地区の GIS 有効フラグ。true なら分析地図にも GIS レイヤーを重畳する。 */
  gisEnabled?: boolean;
  /**
   * docs/24 手書きメモ (要件5): 地区の mapDisplay.memoEnabled が true のとき、分析地図でも
   * 共有手書きメモの表示/非表示・閲覧を可能にする (MapView の ✎ コントロールを surface)。
   * memoUid / memoNickname は投稿地図 (MapPage) と同じ値を渡す。
   */
  memoEnabled?: boolean;
  memoUid?: string | null;
  memoNickname?: string | null;
  memoSessionId?: string | null;
}

const NONE_COLOR_AXIS: MapAxisOption = { key: '', label: '色なし (mode 既定)', kind: 'none' };
const NONE_SIZE_AXIS: MapAxisOption = { key: '', label: 'サイズ一律', kind: 'none' };

/**
 * docs/107 Wave3 / old2 EcwMapView L187 — mapRules 未指定時の既定 colorAxis を mode 依存にする。
 * 旧: walker/bousai/kids-bousai → 評価軸 (= v2 では対象大分類)、それ以外 (ecw) → 感情。
 * これにより地区設定が無くても地図を開いた瞬間に意味のある色分けで表示される
 * (従来は『色なし』固定で全ピン単色だった)。
 */
function pickModeDefaultColorAxis(
  mode: DistrictMode | null | undefined,
  options: MapAxisOption[],
): string {
  const keys = new Set(options.map((o) => o.key));
  const firstClsMajor = options.find((o) => o.kind === 'classification-major')?.key;
  // 【廃止予定 fallback】組込 mode 文字列による既定色軸分岐 (モードドリブン化 MD-4、2026-07-24)。
  // legacy 地区向け。本命は M5 displayRules.map.defaultColorAxis — 設定があればここに到達しない。
  // custom mode は下の firstClsMajor (M3 分類軸) で既に mode 非依存に解決される。
  if (mode === 'walker' || mode === 'bousai' || mode === 'kids-bousai') {
    if (keys.has('category-major')) return 'category-major';
    if (firstClsMajor) return firstClsMajor;
  }
  if (keys.has('emotion')) return 'emotion';
  if (keys.has('category-major')) return 'category-major';
  // custom mode (keikan 等): M3 分類軸の大分類を既定色軸に (意味のある色分けで開く)。
  if (firstClsMajor) return firstClsMajor;
  return options.find((o) => o.kind !== 'none' && o.key)?.key ?? '';
}

/**
 * 値配列 → ColorRule (kind:'value')。各値の色は resolveColor (master colorMap) → palette fallback。
 *
 * old2 整合: 値ごとの色は M2 master の colorMap から解決し、matrix / 一覧 / 詳細の chip 色と一致させる。
 * master 未解決 (resolver 無し / row 不在) の値だけ palette fallback で補う。
 * label をラベル表示用に差し替えたい場合 (例: majorCd → major 名) は resolveLabel を渡す。
 */
function buildValueColorRule(
  values: string[],
  fieldKey: string,
  resolveColor: (value: string) => string | null,
  resolveLabel?: (value: string) => string,
): ColorRuleValue {
  const buckets: ColorRuleBucket[] = values.map((v, i) => ({
    label: resolveLabel ? resolveLabel(v) : v,
    expr: `==${v}`,
    color: resolveColor(v) ?? MODERN_COLOR_PALETTE[i % MODERN_COLOR_PALETTE.length] ?? '#9CA3AF',
  }));
  return {
    kind: 'value',
    fieldKey,
    buckets,
    fallbackColor: '#9CA3AF',
    fallbackLabel: 'その他',
  };
}

/**
 * rating-scale postField → ColorRule (kind:'value')。scale 値で正負 (+ ゼロ) グラデ。
 * 地図の凡例は「正 / 0 / 負」の 3 区分のまま (マトリクスは数値ごとのセルに分かれるが、
 * chip / ピンの色分けは正負で読ませる方針を共有する)。scaleLabels があれば
 * 凡例ラベルに反映する。excludeZero の場合は 0 bucket を出さない。
 */
function buildRatingScaleColorRule(field: FieldDef): ColorRuleValue {
  const min = field.optionsMeta?.min ?? -3;
  const max = field.optionsMeta?.max ?? 3;
  const excludeZero = field.optionsMeta?.excludeZero ?? false;
  const posLabel = field.optionsMeta?.scaleLabels?.posLabel ?? '正';
  const negLabel = field.optionsMeta?.scaleLabels?.negLabel ?? '負';
  const buckets: ColorRuleBucket[] = [];
  if (min < 0) buckets.push({ label: negLabel, expr: '<0', color: '#EF4444' });
  if (!excludeZero && min <= 0 && max >= 0) {
    buckets.push({ label: '0', expr: '==0', color: '#9CA3AF' });
  }
  if (max > 0) buckets.push({ label: posLabel, expr: '>0', color: '#10B981' });
  return {
    kind: 'value',
    fieldKey: field.key,
    buckets,
    fallbackColor: '#9CA3AF',
    fallbackLabel: '未入力',
  };
}

/**
 * 任意の数値 axis → ColorRule (kind:'value')。「負 / 0 / 正」3 bucket (旧 EcwMapView getRatingScaleColor 相当)。
 */
function buildNumericColorRule(fieldKey: string): ColorRuleValue {
  return {
    kind: 'value',
    fieldKey,
    buckets: [
      { label: `${fieldKey} > 0`, expr: '>0', color: '#10B981' },
      { label: `${fieldKey} = 0`, expr: '==0', color: '#9CA3AF' },
      { label: `${fieldKey} < 0`, expr: '<0', color: '#EF4444' },
    ],
    fallbackColor: '#9CA3AF',
    fallbackLabel: '未入力',
  };
}

/** 数値 extraFields の値域 (size axis の正規化レンジ表示用)。 */
function numericRange(posts: PostMinimal[], key: string): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  let found = false;
  for (const p of posts) {
    const v = Number(resolveFieldValue(p as unknown as Record<string, unknown>, key));
    if (!Number.isFinite(v)) continue;
    found = true;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return found ? { min, max } : null;
}

export function AnalysisMapView({
  perspectives,
  defaultPerspectiveLabel,
  posts,
  onSelectPost,
  resolver,
  mapRules,
  districtColorRule,
  matrixRules,
  mode,
  districtId,
  pmtilesMeta,
  onAnalyzeSelection,
  postFields,
  outputFormat,
  gisEnabled,
  memoEnabled,
  memoUid,
  memoNickname,
  memoSessionId,
}: AnalysisMapViewProps) {
  // 位置情報のある投稿のみが地図描画対象 (旧 EcwMapView filteredByLocation 踏襲)。
  const located = useMemo(
    () =>
      posts.filter((p) => p.location && !(p.location.latitude === 0 && p.location.longitude === 0)),
    [posts],
  );

  /* ===== 分析画面と同じ軸駆動フィルタ (master / dataSettings / M5 由来 bucket 絞込) ===== */
  // resolver は任意 (test/後方互換) なので、未指定時は空 resolver で master 軸なしとして扱う。
  const safeResolver = useMemo<AiCategoryMastersResult>(
    () =>
      resolver ?? {
        byCategoryCd: new Map(),
        byCategoryLabel: new Map(),
        byTemplateVar: new Map(),
        masters: [],
        loading: false,
      },
    [resolver],
  );
  const normFields = useMemo(() => (postFields ?? []).map(normalizeFieldDef), [postFields]);
  const filterCustomAxes = useMemo(() => collectCustomAxes(matrixRules), [matrixRules]);
  const filterCustomRuleByKey = useMemo(() => {
    const m = new Map<string, AnalysisAxisRule>();
    for (const c of filterCustomAxes) m.set(c.key, c.rule);
    return m;
  }, [filterCustomAxes]);
  const filterPostFieldAxes = useMemo(
    () => deriveAxisOptionsFromDataSettings(normFields),
    [normFields],
  );
  const filterPostFieldAxisByKey = useMemo(() => {
    const m = new Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>();
    for (const a of filterPostFieldAxes) m.set(a.key, { field: a.field, groups: a.groups });
    return m;
  }, [filterPostFieldAxes]);
  const filterIncludeEmptyKeys = useMemo(() => collectIncludeEmptyKeys(matrixRules), [matrixRules]);
  const filterAxisOptions = useMemo(
    () =>
      buildAxisOptions({
        resolver: safeResolver,
        postFieldAxes: filterPostFieldAxes.map((a) => ({ key: a.key, label: a.label })),
        customAxes: filterCustomAxes.map((c) => ({ key: c.key, label: c.label })),
        hiddenAxisKeys: matrixRules?.hiddenAxisKeys,
        perspectives,
        defaultPerspectiveLabel,
      }),
    [
      safeResolver,
      filterPostFieldAxes,
      filterCustomAxes,
      matrixRules?.hiddenAxisKeys,
      perspectives,
      defaultPerspectiveLabel,
    ],
  );
  const filterAxisDeps = useMemo(
    () => ({
      resolver: safeResolver,
      outputFormat,
      postFieldAxisByKey: filterPostFieldAxisByKey,
      customRuleByKey: filterCustomRuleByKey,
      includeEmptyKeys: filterIncludeEmptyKeys,
      perspectives,
    }),
    [
      perspectives,
      safeResolver,
      outputFormat,
      filterPostFieldAxisByKey,
      filterCustomRuleByKey,
      filterIncludeEmptyKeys,
    ],
  );
  // フィルタチップ用 bucket は located (絞込前) から導出 (絞込で選択肢が消えないように)。
  const filterGroupsByKey = useMemo(() => {
    const m = new Map<string, AxisGroup[]>();
    for (const opt of filterAxisOptions) {
      m.set(
        opt.key,
        buildAxisGroups(opt.key, {
          perspectives,
          resolver: safeResolver,
          posts: located,
          isVertical: false,
          postFieldAxisByKey: filterPostFieldAxisByKey,
          customRuleByKey: filterCustomRuleByKey,
          includeEmptyKeys: filterIncludeEmptyKeys,
        }),
      );
    }
    return m;
  }, [
    perspectives,
    filterAxisOptions,
    safeResolver,
    located,
    filterPostFieldAxisByKey,
    filterCustomRuleByKey,
    filterIncludeEmptyKeys,
  ]);
  const [filterSelections, setFilterSelections] = useState<Record<string, Set<string>>>({});
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = Object.values(filterSelections).filter(
    (s) => (s?.size ?? 0) > 0,
  ).length;
  const toggleFilterValue = (axisKey: string, code: string) => {
    setFilterSelections((prev) => {
      const next = { ...prev };
      const set = new Set(next[axisKey] ?? []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      next[axisKey] = set;
      return next;
    });
  };
  const clearAllFilters = () => setFilterSelections({});

  // フィルタ適用後の located (markers + heatmap 双方の描画対象)。
  const filteredLocated = useMemo(() => {
    const activeKeys = Object.keys(filterSelections).filter(
      (k) => (filterSelections[k]?.size ?? 0) > 0,
    );
    if (activeKeys.length === 0) return located;
    return located.filter((p) => {
      for (const k of activeKeys) {
        const sel = filterSelections[k]!;
        const groups = filterGroupsByKey.get(k) ?? [];
        const codes = postCodesForAxis(p, k, groups, filterAxisDeps);
        let ok = false;
        for (const c of codes) {
          if (sel.has(c)) {
            ok = true;
            break;
          }
        }
        if (!ok) return false;
      }
      return true;
    });
  }, [located, filterSelections, filterGroupsByKey, filterAxisDeps]);

  // 「ラベル表示」: 投稿タイトルを各ピンに重ねる (MapView の showLabels 経由)。
  const [showLabels, setShowLabels] = useState(false);
  // 「▶表示設定」折りたたみ (色分け/サイズ/GIS/マーカー/ヒートマップ/写真/ラベル/フィルタ)。既定は閉。
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Phase 4: 地図からの粗選択モード (onAnalyzeSelection 指定時のみ有効)。
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // 要件1: 自由曲線(投げ縄)選択モード。ON で MapView の lassoEnabled を立て、なぞった範囲内の
  // post id を selectedIds に追加する。
  const [lassoMode, setLassoMode] = useState(false);

  // 要件2/3: 表示方式の設定 (マーカー/ヒートマップ切替・各スライダー・ピン内写真)。
  const [markersVisible, setMarkersVisible] = useState(true);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  // ヒートマップの配色。'color' は色分け軸の色ごとに層を重ね、投稿ピンと同じ色で密度を出す。
  const [heatmapMode, setHeatmapMode] = useState<'density' | 'color'>('density');
  const [heatmapOpacity, setHeatmapOpacity] = useState(0.8);
  const [heatmapRadius, setHeatmapRadius] = useState(25);
  const [markerSizeScale, setMarkerSizeScale] = useState(1);
  const [photoInPin, setPhotoInPin] = useState(false);
  // ピン click: 選択モードなら選択トグル、通常は詳細 modal を開く。
  const handlePinClick = (post: PostMinimal) => {
    if (selectionMode) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(post.id)) next.delete(post.id);
        else next.add(post.id);
        return next;
      });
    } else {
      onSelectPost(post);
    }
  };

  // ===== 利用可能な軸オプションを posts + postFields + mapRules から動的に組み立てる =====
  // docs/105 parity: matrix と同じ軸セット (感情 / 対象大分類(master色) / 対象詳細 / rating-scale /
  // 数値 postField) を共有 util (analysisAxes) で供給する。
  const colorAxisOptions = useMemo<MapAxisOption[]>(() => {
    // 任意 mode の M3 分類軸 大分類 (keikan の景観の観点/対象物 等) を ECW 軸の直後に供給。
    const clsMajorAxes = buildClassificationMajorAxes(outputFormat, resolver);
    const opts = buildColorAxisOptions(
      posts,
      postFields,
      NONE_COLOR_AXIS,
      clsMajorAxes,
      resolver,
      !!districtColorRule,
    );
    // docs/29 §8-1: 追加の観点の AI 解析の軸 (感情・対象など) でも色分けできるようにする。
    if (perspectives && perspectives.length > 0) {
      for (const o of buildAiAxisOptionsWithPerspectives(safeResolver, perspectives, null)) {
        // 既定の観点の AI 解析の軸は上の従来の色分け (感情・対象) が担うので、追加の観点の軸だけ足す。
        if (!o.key.startsWith(PERSPECTIVE_AXIS_PREFIX)) continue;
        opts.push({ key: o.key, label: o.label, kind: 'perspective-axis' });
      }
    }
    // mapRules.defaultColorAxis が options に無ければ numeric 軸として補完 (admin 設定尊重)。
    const def = mapRules?.defaultColorAxis;
    if (def && !opts.some((o) => o.key === def)) {
      opts.push({ key: def, label: def, kind: 'numeric' });
    }
    return opts;
  }, [
    posts,
    postFields,
    mapRules?.defaultColorAxis,
    outputFormat,
    resolver,
    districtColorRule,
    perspectives,
  ]);

  const sizeAxisOptions = useMemo<MapAxisOption[]>(() => {
    const opts = buildSizeAxisOptions(posts, postFields, NONE_SIZE_AXIS);
    const def = mapRules?.defaultSizeAxis;
    if (def && def !== 'count' && !opts.some((o) => o.key === def)) {
      opts.push({ key: def, label: def, kind: 'numeric' });
    }
    return opts;
  }, [posts, postFields, mapRules?.defaultSizeAxis]);

  // 既定色軸の優先順位 (すべて管理画面から制御できる形にする):
  //   1. 地区の色分け設定 (District.mapDisplay.colorRule) があればそれ
  //      → **分析地図の初期表示は投稿アプリの地図と同じ色にする** (ユーザー指示 2026-09-03)。
  //        参加者が見ている色のまま分析に入れるようにするため、分析ツール専用の既定より優先する。
  //   2. M5 `displayRules.map.defaultColorAxis` — 地区に色分け設定が無いときの明示指定
  //   3. mode 依存の既定 (廃止予定 fallback)
  // どの場合も「色分け基準」セレクタで他の軸へ切り替えられる (固定はしない)。
  const initialColorAxis = districtColorRule
    ? DISTRICT_RULE_AXIS.key
    : mapRules?.defaultColorAxis &&
        colorAxisOptions.some((o) => o.key === mapRules.defaultColorAxis)
      ? mapRules.defaultColorAxis
      : pickModeDefaultColorAxis(mode, colorAxisOptions);
  const initialSizeAxis =
    mapRules?.defaultSizeAxis &&
    mapRules.defaultSizeAxis !== 'count' &&
    sizeAxisOptions.some((o) => o.key === mapRules.defaultSizeAxis)
      ? mapRules.defaultSizeAxis
      : '';

  const [colorAxis, setColorAxis] = useState<string>(initialColorAxis);
  const [sizeAxis, setSizeAxis] = useState<string>(initialSizeAxis);

  // ===== 選択 colorAxis → ColorRule (MapView / MapLegend で消費) =====
  // docs/105 parity: kind 別に matrix と同じ色解決 (master colorMap / rating-scale グラデ) を使う。
  const colorRule = useMemo<ColorRule | null>(() => {
    const opt = colorAxisOptions.find((o) => o.key === colorAxis);
    if (!opt) return null;
    const kind: MapAxisKind = opt.kind;
    // 管理画面の設定をそのまま使う。value / quadrant のどちらでも、色・ラベル・アイコンは
    // 投稿アプリと完全に同じになる (下流の evaluateColorRule / MapLegend が共通のため)。
    if (kind === 'district-rule') return districtColorRule ?? null;
    if (kind === 'none') return null;
    if (kind === 'emotion') {
      // old2 整合: emotion label → master colorMap (一覧 / matrix / 詳細の EmotionChips と同経路)。
      // 【廃止予定 fallback】emotion / category / category-major 軸は aiAnalysisEcw (legacy ECW
      // 地区の mode 別 result) 固定 (モードドリブン化 MD-4、2026-07-24)。新規地区は
      // classification-major 軸 (M3 分類軸駆動、任意 mode 対応) が同等の色分けを提供する。
      return buildValueColorRule(
        collectEcwValues(posts, 'emotions'),
        'aiAnalysisEcw.emotions',
        (v) => (resolver ? resolveEmotionColor(resolver, v) : null),
      );
    }
    if (kind === 'category-major') {
      // docs/105 parity 追加: 対象カテゴリ 大分類 (majorCd) を master colorMap 色で。
      // pin 値は「categoryCodes 先頭の majorCd」を見るため fieldKey は専用の合成 path。
      // MapView/MapLegend は ColorRule.buckets の色を凡例に並べるだけなので fieldKey は表示専用。
      const resolver0 = resolver;
      return buildValueColorRule(
        resolver0 ? collectCategoryMajorValues(posts, resolver0) : [],
        'aiAnalysisEcw.categoryMajor',
        (v) => (resolver0 ? resolveCategoryMajorColor(resolver0, v) : null),
        (v) => (resolver0 ? resolveCategoryMajorLabel(resolver0, v) : v),
      );
    }
    if (kind === 'category') {
      // old2 整合: categoryCd → master colorMap / row.color (一覧 / matrix / 詳細の CategoryChips と同経路)。
      return buildValueColorRule(
        collectEcwValues(posts, 'categoryCodes'),
        'aiAnalysisEcw.categoryCodes',
        (v) => (resolver ? resolveCategoryColor(resolver, v) : null),
      );
    }
    if (kind === 'classification-major' && opt.master && opt.classificationAxisKey) {
      // 任意 mode の M3 分類軸 大分類: 代表(rank最小) majorCode を master colorMap 色で。
      // pin 値は派生 key (mapPosts に注入) を見るので fieldKey は CLS_MAJOR_FIELD_KEY 固定。
      const master = opt.master;
      const axisKey = opt.classificationAxisKey;
      return buildValueColorRule(
        collectRepresentativeMajorValues(located, outputFormat, mode, axisKey),
        CLS_MAJOR_FIELD_KEY,
        (v) => resolveMasterMajorColor(master, v),
        (v) => resolveMasterMajorLabel(master, v),
      );
    }
    if (kind === 'perspective-axis') {
      // マトリクスと同じグループ (その観点のカテゴリマスタの大分類・分類) と色。代表値は mapPosts に注入。
      const groups = buildAxisGroups(opt.key, {
        perspectives,
        resolver: safeResolver,
        posts,
        isVertical: false,
        postFieldAxisByKey: EMPTY_GROUP_MAP,
        customRuleByKey: EMPTY_GROUP_MAP,
        includeEmptyKeys: EMPTY_KEY_SET,
      });
      return buildValueColorRule(
        groups.map((g) => g.code),
        PERSPECTIVE_AXIS_FIELD_KEY,
        (v) => groups.find((g) => g.code === v)?.color ?? null,
        (v) => groups.find((g) => g.code === v)?.label ?? v,
      );
    }
    if (kind === 'rating-scale' && opt.field) {
      // rating-scale postField: scale 値で正負 (+ ゼロ) グラデ (matrix の値別セルと同じ正負配色)。
      return buildRatingScaleColorRule(opt.field);
    }
    if (kind === 'select-value') {
      // select / multiselect postField: 選択肢ごとに value bucket (palette 色)。
      // custom mode (例 keikan) の KeikanType 等を地図で色分け可能にする。
      return buildValueColorRule(collectSelectValues(posts, opt.key), opt.key, () => null);
    }
    // numeric (任意の数値 postField / extraFields): 負 / 0 / 正 の 3 bucket。
    return buildNumericColorRule(opt.key);
  }, [colorAxis, colorAxisOptions, posts, located, resolver, outputFormat, mode, perspectives]);

  // ===== classification-major 軸選択時、各 located post に代表 majorCode を派生注入 =====
  // evaluateColorRule は post の fieldKey を見るため、generic 分類大分類の色を pin に反映するには
  // 代表(rank最小)の majorCode を resolvable な path (extraFields[CLS_MAJOR_FIELD_KEY]) に載せる必要がある。
  // 既存データは一切書き換えず、地図描画用のローカル複製にのみ派生 field を足す (matrix の projectedPosts と同方針)。
  // フィルタ適用後の filteredLocated を地図に描画する (markers + heatmap が連動)。
  const mapPosts = useMemo<PostMinimal[]>(() => {
    const opt = colorAxisOptions.find((o) => o.key === colorAxis);
    if (opt?.kind === 'perspective-axis') {
      const pa = resolvePerspectiveAxis(opt.key, perspectives);
      if (!pa.perspective) return filteredLocated;
      const perspective = pa.perspective;
      return filteredLocated.map((p) => {
        const first = [...perspectiveAxisCodes(p, perspective, pa.baseKey)][0] ?? null;
        return {
          ...p,
          extraFields: { ...(p.extraFields ?? {}), [PERSPECTIVE_AXIS_FIELD_KEY]: first },
        };
      });
    }
    if (!opt || opt.kind !== 'classification-major' || !opt.classificationAxisKey) {
      return filteredLocated;
    }
    const axisKey = opt.classificationAxisKey;
    return filteredLocated.map((p) => ({
      ...p,
      extraFields: {
        ...(p.extraFields ?? {}),
        [CLS_MAJOR_FIELD_KEY]: resolveRepresentativeMajor(p, outputFormat, mode, axisKey),
      },
    }));
  }, [filteredLocated, colorAxis, colorAxisOptions, outputFormat, mode, perspectives]);

  // ===== 選択 sizeAxis の正規化レンジ + 対象件数 (コントロールパネル / 凡例表示用) =====
  const sizeSummary = useMemo(() => {
    if (!sizeAxis) return null;
    const range = numericRange(located, sizeAxis);
    if (!range) return { range: null, count: 0 };
    const count = located.filter((p) =>
      Number.isFinite(Number(resolveFieldValue(p as unknown as Record<string, unknown>, sizeAxis))),
    ).length;
    return { range, count };
  }, [sizeAxis, located]);

  // docs/107 Wave3 / old2 EcwMapView L543-557 — スコア連動可変ピンサイズ (要件4: 投稿の段階別データで動的サイズ)。
  // sizeAxis 選択時、|値|/maxAbs を 6〜24px に正規化して post.id→半径 map を作り MapView へ渡す。
  // 正規化基準 (maxAbs) は old2 踏襲で「段階の定義範囲 (rating-scale postField の optionsMeta.min/max)」を
  // 優先する。これにより外れ値 1 件で巨大ピンになる事故を防ぎ、段階(スコア)の大小がサイズに比例する。
  // 定義範囲が無い軸 (素の数値 extraFields 等) は実データの値域で正規化する。
  // sizeAxis 未選択なら undefined → MapView は全ピン既定 10px (従来同等)。
  const radiusById = useMemo<ReadonlyMap<string, number> | undefined>(() => {
    if (!sizeAxis) return undefined;
    const opt = sizeAxisOptions.find((o) => o.key === sizeAxis);
    const meta = opt?.field?.optionsMeta;
    // 段階の定義範囲 (rating-scale 等) があればそれを正規化基準にする (要件4「段階別データ」)。
    const scaleMaxAbs =
      meta && (typeof meta.min === 'number' || typeof meta.max === 'number')
        ? Math.max(Math.abs(meta.min ?? -3), Math.abs(meta.max ?? 3), 1e-6)
        : null;
    let maxAbs = scaleMaxAbs;
    if (maxAbs === null) {
      const range = numericRange(located, sizeAxis);
      if (!range) return undefined;
      maxAbs = Math.max(Math.abs(range.min), Math.abs(range.max), 1e-6);
    }
    const MIN_R = 6;
    const MAX_R = 24;
    const m = new Map<string, number>();
    for (const p of located) {
      const v = Number(resolveFieldValue(p as unknown as Record<string, unknown>, sizeAxis));
      if (!Number.isFinite(v)) {
        m.set(p.id, 10);
        continue;
      }
      // |v| を定義範囲で clamp してから正規化 (外れ値で MAX_R を超えないように)。
      const norm = Math.min(maxAbs, Math.abs(v)) / maxAbs;
      m.set(p.id, Math.round(MIN_R + norm * (MAX_R - MIN_R)));
    }
    return m;
  }, [sizeAxis, sizeAxisOptions, located]);

  // docs/22 GIS overlay (要件4): 分析地図にも GIS レイヤーを重畳 + feature 属性パネル。
  const gis = useDistrictGisLayers(districtId, gisEnabled === true);
  const [gisFeature, setGisFeature] = useState<GisFeatureClickInfo | null>(null);
  // 要件5: GIS レイヤーを一括で表示/非表示するトグル (既定 ON)。OFF で overlays を空にする。
  const [showGis, setShowGis] = useState(true);
  const gisOverlays = showGis ? gis.overlays : [];

  const centerOverride = mapRules?.defaultCenter;
  const zoomOverride = mapRules?.defaultZoom;

  return (
    <div className="h-full flex flex-col">
      {/* ===== 操作行 (常時表示): ▶表示設定 トグル + 粗選択コントロール + 📊分析(折りたたみ外) ===== */}
      <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          aria-expanded={settingsOpen}
          className={`shrink-0 text-xs px-2.5 py-1 rounded font-semibold border min-h-touch ${
            settingsOpen || activeFilterCount > 0
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
          }`}
        >
          {settingsOpen ? '▼' : '▶'} 表示設定
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>

        {/* 粗選択コントロール (onAnalyzeSelection 時): ピン選択 / 自由曲線 / クリア */}
        {onAnalyzeSelection && (
          <>
            <label className="flex items-center gap-1.5 text-xs text-primary-800 cursor-pointer">
              <input
                type="checkbox"
                checked={selectionMode}
                onChange={(e) => setSelectionMode(e.target.checked)}
              />
              🔍 ピンをタップで選択
            </label>
            {/* 要件1: 自由曲線(投げ縄)で範囲選択。ON 中は地図ドラッグの代わりになぞって囲む。 */}
            <button
              type="button"
              aria-pressed={lassoMode}
              onClick={() => setLassoMode((v) => !v)}
              className={`text-xs px-2 py-0.5 rounded font-semibold border transition-colors min-h-touch ${
                lassoMode
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-primary-700 border-primary-300 hover:bg-primary-100'
              }`}
            >
              {lassoMode ? '✋ なぞって囲む（ON）' : '✏️ 自由曲線で選択'}
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-[11px] underline text-primary-700 hover:text-primary-900"
              >
                クリア
              </button>
            )}
          </>
        )}

        {/* 📊 分析 (折りたたみ外、右端)。 */}
        {onAnalyzeSelection && (
          <button
            type="button"
            disabled={selectedIds.size === 0}
            onClick={() => onAnalyzeSelection?.(Array.from(selectedIds))}
            className="ml-auto text-xs px-3 py-1.5 rounded-md font-bold text-white shadow-md ring-2 ring-accent-600/40 bg-accent-600 hover:bg-accent-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed min-h-touch"
          >
            📊 分析 ({selectedIds.size})
          </button>
        )}
      </div>

      {/* ===== ▶表示設定 折りたたみ (inline = モバイルでも必ず画面内、max-h でスクロール) =====
          色分け / サイズ / GIS / マーカー / ラベル / 写真 / ヒートマップ / フィルタ をすべて格納。 */}
      <div
        className={`${settingsOpen ? 'flex' : 'hidden'} shrink-0 flex-col gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 max-h-[55vh] overflow-y-auto`}
      >
        {/* 色分け + サイズ + GIS */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <span className="font-medium">色分け</span>
            <select
              aria-label="色分け基準 (colorAxis)"
              value={colorAxis}
              onChange={(e) => setColorAxis(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-xs bg-white min-h-touch max-w-[45vw] sm:max-w-none"
            >
              {colorAxisOptions.map((o) => (
                <option key={o.key || 'none'} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <span className="font-medium">サイズ</span>
            <select
              aria-label="サイズ基準 (sizeAxis)"
              value={sizeAxis}
              onChange={(e) => setSizeAxis(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-xs bg-white min-h-touch max-w-[45vw] sm:max-w-none"
            >
              {sizeAxisOptions.map((o) => (
                <option key={o.key || 'none'} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {sizeAxis && sizeSummary && (
            <span className="text-[11px] text-slate-500" data-testid="size-axis-summary">
              {sizeSummary.range
                ? `${sizeAxis}: ${sizeSummary.range.min} 〜 ${sizeSummary.range.max} (${sizeSummary.count} 件)`
                : `${sizeAxis}: 該当データなし`}
            </span>
          )}
          {gis.hasLayers && (
            <button
              type="button"
              onClick={() => setShowGis((v) => !v)}
              aria-pressed={showGis}
              className={`text-xs px-2.5 py-1 rounded font-semibold border min-h-touch ${
                showGis
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              🗺 GIS{showGis ? '表示中' : '非表示'}
            </button>
          )}
        </div>

        {/* マーカー / ラベル / 写真 / マーカーサイズ */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 border-t border-slate-100">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={markersVisible}
              onChange={(e) => setMarkersVisible(e.target.checked)}
            />
            マーカー(投稿ピン)を表示
          </label>
          {/* 要件: ラベル表示 = 投稿タイトルをピンに重ねる。 */}
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
            />
            ラベル表示 (投稿タイトル)
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={photoInPin}
              onChange={(e) => setPhotoInPin(e.target.checked)}
              disabled={!markersVisible}
            />
            ピン内に写真を表示
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <span className="shrink-0">マーカーサイズ</span>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.1}
              value={markerSizeScale}
              onChange={(e) => setMarkerSizeScale(Number(e.target.value))}
              disabled={!markersVisible}
              className="w-24 cursor-pointer disabled:opacity-50"
              aria-label="マーカーサイズ倍率"
            />
            <span className="w-8 text-right tabular-nums">×{markerSizeScale.toFixed(1)}</span>
          </label>
        </div>

        {/* ヒートマップ + 透明度 + 影響範囲 */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={heatmapEnabled}
              onChange={(e) => setHeatmapEnabled(e.target.checked)}
            />
            ヒートマップを表示
          </label>
          {/* 配色の切替。'color' は投稿ピンと同じ色ごとに層を重ね、色別の集まり方を見る。
              'density' は従来の青→赤で件数の多さだけを見る。 */}
          <div
            className="flex items-center gap-2 text-xs text-slate-600"
            role="radiogroup"
            aria-label="ヒートマップの配色"
          >
            <span className="shrink-0">配色</span>
            {(
              [
                { key: 'density', label: '密度' },
                { key: 'color', label: 'ピンの色' },
              ] as const
            ).map((o) => (
              <label
                key={o.key}
                className={`flex items-center gap-1 cursor-pointer ${
                  heatmapEnabled ? '' : 'opacity-50'
                }`}
              >
                <input
                  type="radio"
                  name="heatmap-mode"
                  value={o.key}
                  checked={heatmapMode === o.key}
                  onChange={() => setHeatmapMode(o.key)}
                  disabled={!heatmapEnabled}
                />
                {o.label}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <span className="shrink-0">透明度</span>
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.1}
              value={heatmapOpacity}
              onChange={(e) => setHeatmapOpacity(Number(e.target.value))}
              disabled={!heatmapEnabled}
              className="w-20 cursor-pointer disabled:opacity-50"
              aria-label="ヒートマップの不透明度"
            />
            <span className="w-8 text-right tabular-nums">{heatmapOpacity.toFixed(1)}</span>
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <span className="shrink-0">影響範囲</span>
            <input
              type="range"
              min={5}
              max={80}
              step={1}
              value={heatmapRadius}
              onChange={(e) => setHeatmapRadius(Number(e.target.value))}
              disabled={!heatmapEnabled}
              className="w-20 cursor-pointer disabled:opacity-50"
              aria-label="ヒートマップの影響範囲(半径)"
            />
            <span className="w-8 text-right tabular-nums">{heatmapRadius}</span>
          </label>
        </div>

        {/* フィルタ (分析画面と同じ軸駆動 bucket chip)。markers + heatmap 双方が連動。 */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`text-xs px-2.5 py-1 rounded font-semibold min-h-touch ${
              showFilters || activeFilterCount > 0
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {showFilters ? '▲ フィルタを閉じる' : '▼ フィルタ'}
            {activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-[11px] text-slate-500 hover:text-slate-700 underline"
            >
              フィルタ解除
            </button>
          )}
        </div>
        {showFilters && (
          <div className="flex flex-col gap-2">
            {filterAxisOptions.length === 0 && (
              <p className="text-xs text-slate-400">絞込に使える軸がありません。</p>
            )}
            {filterAxisOptions.map((opt) => {
              const groups = filterGroupsByKey.get(opt.key) ?? [];
              if (groups.length === 0) return null;
              const sel = filterSelections[opt.key] ?? new Set<string>();
              return (
                <div key={opt.key} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-600 shrink-0 min-w-[5rem]">
                    {opt.label}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {groups.map((g) => {
                      const checked = sel.has(g.code);
                      return (
                        <button
                          key={g.code}
                          type="button"
                          onClick={() => toggleFilterValue(opt.key, g.code)}
                          aria-pressed={checked}
                          className="px-2 py-0.5 rounded-full text-[11px] min-h-touch border"
                          style={
                            checked
                              ? {
                                  backgroundColor: g.color,
                                  color: contrastTextColor(g.color),
                                  borderColor: g.color,
                                }
                              : {
                                  backgroundColor: '#f1f5f9',
                                  color: '#334155',
                                  borderColor: '#e2e8f0',
                                }
                          }
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== 本体 (地図 or 空メッセージ) ===== */}
      {located.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-slate-500 text-center">
            {posts.length === 0
              ? '表示できる投稿がありません。'
              : '位置情報のある投稿がありません。'}
          </p>
        </div>
      ) : (
        <div className="flex-1 relative overflow-hidden">
          <MapView
            posts={mapPosts}
            onPinClick={handlePinClick}
            districtId={districtId}
            pmtilesMeta={pmtilesMeta}
            clusterEnabled={false}
            selectedIds={selectedIds}
            radiusById={radiusById}
            mode={mode}
            colorRule={colorRule}
            showUserLocation={false}
            centerOverride={centerOverride}
            zoomOverride={zoomOverride}
            gisOverlays={gisOverlays}
            onGisFeatureClick={setGisFeature}
            memoEnabled={memoEnabled}
            memoUid={memoUid}
            memoNickname={memoNickname}
            memoSessionId={memoSessionId}
            lassoEnabled={lassoMode && !!onAnalyzeSelection}
            onLassoSelect={(ids) =>
              setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const id of ids) next.add(id);
                return next;
              })
            }
            markersVisible={markersVisible}
            markerSizeScale={markerSizeScale}
            heatmapEnabled={heatmapEnabled}
            heatmapMode={heatmapMode}
            heatmapOpacity={heatmapOpacity}
            heatmapRadius={heatmapRadius}
            photoInPin={photoInPin}
            // 要件: ラベル表示 (投稿タイトル)。
            showLabels={showLabels}
          />
          <MapLegend mode={mode} colorRule={colorRule} bottomClassName="bottom-2" />
          <GisFeaturePanel info={gisFeature} onClose={() => setGisFeature(null)} />
        </div>
      )}
    </div>
  );
}

// 旧 MapControls / MapDisplayPopover (絶対配置 popover) は ▶表示設定 inline 折りたたみに統合し撤去。
